import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Award, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RANK_STYLES = [
  { badge: 'bg-gradient-to-br from-yellow-400 to-orange-500 text-black shadow-lg shadow-yellow-500/40', ring: 'ring-2 ring-yellow-400/50' },
  { badge: 'bg-gradient-to-br from-slate-300 to-slate-500 text-black', ring: 'ring-1 ring-slate-300/40' },
  { badge: 'bg-gradient-to-br from-amber-600 to-amber-800 text-white', ring: 'ring-1 ring-amber-500/40' },
];

export const ReferralLeaderboard = () => {
  const [leaders, setLeaders] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/waitlist/leaderboard`);
        if (cancelled) return;
        setLeaders(data.leaderboard || []);
        setThreshold(data.threshold || 3);
      } catch (e) {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide entire section if nobody has referred anyone yet - avoids empty embarrassment
  if (loading || leaders.length === 0) return null;

  return (
    <section
      className="py-16 relative overflow-hidden"
      data-testid="leaderboard-section"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black" />
      <div className="container relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/30">
              <Trophy className="w-5 h-5 text-black" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-yellow-300 font-semibold">
                Top Referrers
              </span>
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">
                Bring the most friends. Get bragging rights.
              </h2>
            </div>
          </div>

          <div
            className="glass-card p-3 md:p-4 border border-yellow-500/10"
            data-testid="leaderboard-list"
          >
            <ul className="divide-y divide-white/5">
              {leaders.map((leader) => {
                const style = RANK_STYLES[leader.rank - 1] || {};
                const progress = Math.min(leader.referral_count / threshold, 1);
                return (
                  <li
                    key={leader.rank}
                    className="flex items-center gap-4 py-3 md:py-4 px-2"
                    data-testid={`leaderboard-row-${leader.rank}`}
                  >
                    <div
                      className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${style.badge || 'bg-white/5 text-gray-300'} ${style.ring || ''}`}
                    >
                      {leader.rank}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white truncate">
                          {leader.display_name}
                        </span>
                        {leader.bonus_unlocked && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-pink-500/20 text-pink-300 border border-pink-500/30 uppercase tracking-wider font-semibold"
                            data-testid={`leaderboard-unlocked-${leader.rank}`}
                          >
                            <Award className="w-3 h-3" /> Unlocked
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-cyan-400"
                          style={{ width: `${progress * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <div className="text-xl md:text-2xl font-bold text-white leading-tight">
                        {leader.referral_count}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">
                        Referrals
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <p className="text-xs text-gray-500 mt-4 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Refer {threshold} friends to unlock the bonus track. Grab your share link after joining the waitlist above.
          </p>
        </div>
      </div>
    </section>
  );
};
